import { StrictMode } from "react";
import { hydrateRoot } from "react-dom/client";
import { BOARD_MAX, BOARD_MIN } from "../shared/prompts/board-rules";
import { BOARD_PICK_COPY, BUILDER_COPY, PACK_COPY, fillCopy } from "../shared/prompts/copy";
import { getTemplate } from "../shared/prompts/templates";
import { BuilderIsland } from "./components/BuilderIsland";
import { copyText } from "./lib/clipboard";
import { pushPbEvent } from "./lib/gtm-events";
import { createBuilderStore, initialState, parseSeed, type BuilderStore } from "./lib/store";
import "./styles.css";

const COPIED_RESET_MS = 1800;

// The library's live region is server HTML. Clearing it first lets a repeat of the same
// message announce again.
function announce(message: string): void {
  const region = document.getElementById("pb-library-status");
  if (!region) return;
  region.textContent = "";
  window.setTimeout(() => {
    region.textContent = message;
  }, 50);
}

// Server-rendered copy buttons (the hub library and the pack page): data-pb-copy names the
// element whose text is copied.
function wireCopyButtons(): void {
  document.addEventListener("click", (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const copyButton = target?.closest<HTMLButtonElement>("button[data-pb-copy]");
    if (!copyButton) return;
    const source = document.getElementById(copyButton.dataset.pbCopy ?? "");
    const label = copyButton.querySelector<HTMLElement>("[data-label]");
    if (!source || !label) return;
    void copyText(source.textContent ?? "").then((ok) => {
      announce(ok ? BUILDER_COPY.copied : BUILDER_COPY.copyFailed);
      if (!ok) return;
      const idle = copyButton.dataset.idleLabel ?? label.textContent ?? "";
      copyButton.dataset.idleLabel = idle;
      copyButton.dataset.state = "copied";
      label.textContent = BUILDER_COPY.copied;
      if (copyButton.dataset.pbSurface !== "board_link") {
        pushPbEvent("prompt_copied", {
          prompt_id: copyButton.dataset.pbPrompt,
          surface: copyButton.dataset.pbSurface ?? "library",
        });
      }
      window.setTimeout(() => {
        copyButton.dataset.state = "";
        label.textContent = idle;
      }, COPIED_RESET_MS);
    });
  });
}

// The prompt library is server HTML outside the island: wire its "Use this prompt" links
// to the island's store.
function wireLibrary(store: BuilderStore): void {
  document.addEventListener("click", (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const useLink = target.closest<HTMLAnchorElement>("a[data-pb-use]");
    if (useLink) {
      const template = getTemplate(useLink.dataset.pbUse ?? "");
      if (!template) return;
      event.preventDefault();
      store.set((s) => ({
        ...s,
        inputs: { ...s.inputs, goal: template.category, promptId: template.id },
      }));
      pushPbEvent("input_changed", { field: "prompt", value: template.id, source: "library" });
      const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
      document
        .getElementById("builder")
        ?.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" });
    }
  });
}

// The hub library's goals are closed accordions. A goal chip or a #cat-<goal> deep link opens
// its accordion, so the jump lands on the prompts rather than a closed row.
function openGoal(hash: string): HTMLDetailsElement | null {
  if (!hash.startsWith("#cat-")) return null;
  let id: string;
  try {
    id = decodeURIComponent(hash.slice(1));
  } catch {
    return null;
  }
  const target = document.getElementById(id);
  if (!(target instanceof HTMLDetailsElement)) return null;
  target.open = true;
  return target;
}

function wireGoalAccordions(): void {
  document.addEventListener("click", (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const link = target?.closest<HTMLAnchorElement>('a[href^="#cat-"]');
    if (link) openGoal(link.hash);
  });
  const fromLocation = () => openGoal(window.location.hash)?.scrollIntoView({ block: "start" });
  window.addEventListener("hashchange", fromLocation);
  fromLocation();
}

// The pack page's board picker. The server enforces 5 to 10 too; this keeps the count live.
function wireBoardPicker(): void {
  const form = document.querySelector<HTMLFormElement>("form[data-pb-board]");
  if (!form) return;
  const count = form.querySelector<HTMLElement>("[data-pb-board-count]");
  const hint = form.querySelector<HTMLElement>("[data-pb-board-hint]");
  const bar = form.querySelector<HTMLElement>("[data-pb-board-bar]");
  const picked = () => form.querySelectorAll<HTMLInputElement>('input[name="pick"]:checked').length;
  const sync = (alert: boolean) => {
    if (count) count.textContent = fillCopy(BOARD_PICK_COPY.counter, { n: String(picked()) });
    bar?.toggleAttribute("data-pb-board-empty", picked() === 0 && !alert);
  };
  const show = (message: string, alert: boolean) => {
    sync(alert);
    if (!hint) return;
    hint.textContent = message;
    hint.classList.toggle("font-semibold", alert);
    hint.classList.toggle("text-[#962f22]", alert);
    hint.classList.toggle("text-neutral-600", !alert);
    hint.classList.toggle("max-sm:hidden", !alert);
  };
  // A reload can restore ticked boxes the server did not render.
  if (picked() > 0) sync(false);
  form.addEventListener("change", (event) => {
    const box = event.target;
    if (box instanceof HTMLInputElement && box.name === "pick" && box.checked) {
      if (picked() > BOARD_MAX) {
        box.checked = false;
        show(BOARD_PICK_COPY.tooMany, true);
        return;
      }
    }
    show(BOARD_PICK_COPY.privacy, false);
  });
  form.addEventListener("submit", (event) => {
    if (picked() >= BOARD_MIN) return;
    event.preventDefault();
    show(BOARD_PICK_COPY.tooFew, true);
  });
}

const PDF_POLL_MS = 3000;
const PDF_POLL_MAX_ERRORS = 5;
const PDF_TONES = {
  rendering: ["bg-white", "text-neutral-800", "ring-neutral-950/5"],
  ready: ["bg-[#E7F8F2]", "text-[#0A6F4D]", "ring-[#9FE3CD]"],
  failed: ["bg-[#FFEDEB]", "text-[#962f22]", "ring-[#F6CFC9]"],
} as const;

// The pack page's "nearly ready" note: poll the render's state and swap in the download link
// or the failure line. The server's in-flight window ends every render, so polling stops.
function wirePdfStatus(): void {
  const note = document.querySelector<HTMLElement>("[data-pb-pdf-status]");
  const statusUrl = note?.dataset.pbPdfStatus;
  const href = note?.dataset.pbPdfHref;
  if (!note || !statusUrl || !href) return;
  // The note's leading icon stays; only the text after it is swapped.
  const icon = note.querySelector(".pb-duo");
  const show = (state: "ready" | "failed") => {
    note.classList.remove(...PDF_TONES.rendering);
    note.classList.add(...PDF_TONES[state]);
    const text = document.createElement("span");
    if (state === "failed") {
      text.textContent = PACK_COPY.pdfFailed;
    } else {
      const link = document.createElement("a");
      link.href = href;
      link.className = "font-semibold underline underline-offset-2";
      link.setAttribute("referrerpolicy", "no-referrer");
      link.textContent = PACK_COPY.pdfReadyLink;
      text.append(`${PACK_COPY.pdfReady} `, link);
    }
    note.replaceChildren(...(icon ? [icon] : []), text);
  };
  let errors = 0;
  const poll = async () => {
    let state: unknown;
    try {
      const res = await fetch(statusUrl, { cache: "no-store", credentials: "same-origin" });
      state = ((await res.json()) as { state?: unknown }).state;
      errors = 0;
    } catch {
      errors += 1;
      state = errors >= PDF_POLL_MAX_ERRORS ? "failed" : "rendering";
    }
    if (state === "ready") show("ready");
    else if (state === "rendering") window.setTimeout(() => void poll(), PDF_POLL_MS);
    else show("failed");
  };
  window.setTimeout(() => void poll(), PDF_POLL_MS);
}

wireCopyButtons();
wireGoalAccordions();
wireBoardPicker();
wirePdfStatus();

const root = document.getElementById("pb-root");
if (root) {
  const seed = parseSeed(root.dataset.pbSeed);
  const store = createBuilderStore(initialState(seed));
  hydrateRoot(
    root,
    <StrictMode>
      <BuilderIsland
        store={store}
        seed={seed}
        turnstileSiteKey={root.dataset.pbSitekey ?? ""}
        appEnv={root.dataset.pbEnv ?? ""}
      />
    </StrictMode>,
  );
  wireLibrary(store);
}
